import { notification } from 'antd';
import { data_from_server_post } from '@/shared/http/client';
import { getOrCreateClientUUID } from '@/shared/utils/helpers';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { mapEcrOverallProgress } from '@/shared/utils/downloadProgressUtils';

const INSTANCES_CAP_ERROR_RE = /^Up to \d+ instances on /;

export function isInstancesCapError(message) {
    return typeof message === 'string' && INSTANCES_CAP_ERROR_RE.test(message.trim());
}

function notifyDownloadFailure(message) {
    notification.error({
        message: 'Download Failed',
        description: message || 'Failed to start model export',
        duration: 6,
    });
}

function handleDownloadFailure(message, { onInstancesCapError, onError } = {}) {
    const detail = message || 'Failed to start model export';
    if (isInstancesCapError(detail) && onInstancesCapError) {
        onInstancesCapError(detail);
        return;
    }
    if (onError) {
        onError(detail);
        return;
    }
    notifyDownloadFailure(detail);
}

export function triggerModelDownload({
    clientId,
    usecaseId,
    version,
    setProgressStatus,
    artifactType = 'zip',
    instanceGroupCount = 2,
    device = 'cpu',
    cloudProvider = null,
    hardwareDevice = null,
    accelerator = null,
    instanceType = null,
    longInputProcessing = null,
    predictionStrategy = null,
    preferredInfrastructure = false,
    outputFilename = null,
    ecrRepositoryUri = null,
    ecrAccountMode = null,
    ecrAlias = null,
    gcrRepositoryUri = null,
    gcrAuthMode = null,
    gcrAlias = null,
    dockerhubRepository = null,
    dockerhubAlias = null,
    forcePush = false,
    onSuccess,
    onInstancesCapError,
    onError,
    onDuplicatePush,
}) {
    data_from_server_post(
        "download-model-request",
        {
            client_id: clientId || DEFAULT_CLIENT_ID,
            llm_usecase_id: usecaseId,
            deployed_version: version,
            client_uuid: getOrCreateClientUUID(),
            artifact_type: artifactType,
            instance_group_count: instanceGroupCount,
            device,
            cloud_provider: cloudProvider,
            hardware_device: hardwareDevice,
            accelerator,
            instance_type: instanceType,
            long_input_processing: longInputProcessing,
            prediction_strategy: predictionStrategy,
            preferred_infrastructure: preferredInfrastructure,
            output_filename: outputFilename,
            ecr_repository_uri: ecrRepositoryUri,
            ecr_account_mode: ecrAccountMode,
            ecr_alias: ecrAlias,
            gcr_repository_uri: gcrRepositoryUri,
            gcr_auth_mode: gcrAuthMode,
            gcr_alias: gcrAlias,
            dockerhub_repository: dockerhubRepository,
            dockerhub_alias: dockerhubAlias,
            force_push: forcePush,
        },
        (response) => {
            // A prior push of this exact image was found: surface a confirm
            // prompt instead of treating it as a hard failure.
            if (!response.success && response.duplicate) {
                if (onDuplicatePush) {
                    onDuplicatePush(response);
                    return;
                }
            }
            if (response.success) {
                if (response.progressInfo) {
                    const registryPhased =
                        artifactType === 'ecr' ||
                        artifactType === 'gcr' ||
                        artifactType === 'dockerhub'
                            ? mapEcrOverallProgress(response.progressInfo.progress)
                            : {
                                phase: 'building',
                                buildProgress: response.progressInfo.progress,
                                downloadProgress: 0,
                            };
                    setProgressStatus(prev => ({
                        ...prev,
                        [response.progressInfo.downloadId]: {
                            progress: response.progressInfo.progress,
                            ...registryPhased,
                            isCompleted: response.progressInfo.isCompleted,
                            isError: response.progressInfo.isError,
                            datasetName: response.progressInfo.modelName,
                            usecaseId: response.progressInfo.usecaseId,
                            message: response.progressInfo.message,
                            isDownload: true,
                            downloadId: response.progressInfo.downloadId,
                            version: response.progressInfo.version,
                            clientId: response.progressInfo.clientId,
                            artifactType,
                        }
                    }));
                }
                onSuccess?.(response);
            } else {
                handleDownloadFailure(response.message, { onInstancesCapError, onError });
            }
        },
        (error) => {
            console.error('Export model error:', error);
            handleDownloadFailure(error, { onInstancesCapError, onError });
        }
    );
}

export function triggerBundledModelDownload({
    clientId,
    bundledTasks,
    setProgressStatus,
    artifactType = 'docker',
    instanceGroupCount = 2,
    device = 'cpu',
    cloudProvider = null,
    hardwareDevice = null,
    accelerator = null,
    instanceType = null,
    preferredInfrastructure = false,
    outputFilename = null,
    onSuccess,
    onInstancesCapError,
    onError,
}) {
    const primary = bundledTasks[0];
    data_from_server_post(
        'download-model-request',
        {
            client_id: clientId || DEFAULT_CLIENT_ID,
            llm_usecase_id: primary.llm_usecase_id,
            deployed_version: primary.deployed_version,
            client_uuid: getOrCreateClientUUID(),
            artifact_type: artifactType,
            instance_group_count: instanceGroupCount,
            device,
            cloud_provider: cloudProvider,
            hardware_device: hardwareDevice,
            accelerator,
            instance_type: instanceType,
            preferred_infrastructure: preferredInfrastructure,
            output_filename: outputFilename,
            bundled_tasks: bundledTasks,
        },
        (response) => {
            if (response.success) {
                if (response.progressInfo) {
                    setProgressStatus(prev => ({
                        ...prev,
                        [response.progressInfo.downloadId]: {
                            progress: response.progressInfo.progress,
                            buildProgress: response.progressInfo.progress,
                            downloadProgress: 0,
                            phase: 'building',
                            isCompleted: response.progressInfo.isCompleted,
                            isError: response.progressInfo.isError,
                            datasetName: response.progressInfo.modelName,
                            usecaseId: response.progressInfo.usecaseId,
                            message: response.progressInfo.message,
                            isDownload: true,
                            downloadId: response.progressInfo.downloadId,
                            version: response.progressInfo.version,
                            clientId: response.progressInfo.clientId,
                            artifactType,
                        }
                    }));
                }
                onSuccess?.(response);
            } else {
                handleDownloadFailure(response.message, { onInstancesCapError, onError });
            }
        },
        (error) => {
            console.error('Bundled export model error:', error);
            handleDownloadFailure(error, { onInstancesCapError, onError });
        }
    );
}
